import { useEffect, useRef, useState } from 'react'
import Icon from '../Icon'
import Reveal from '../Reveal'
import { Section, SectionHead } from '../Section'

const VIDEO = '/media/casa-film.mp4'
const POSTER = '/images/casa-film-poster.jpg'

/**
 * The explainer film on the home page.
 *
 * Deliberately **not** an autoplaying background loop. The file is a 58-second
 * narrated animation with an audio track, so it is something to watch rather
 * than decoration, and three things follow from that:
 *
 *   - No `autoplay`, and the `<video>` is not even created until someone presses
 *     play. The file is 12 MB; nobody who scrolls past should pay for it, and on
 *     a phone contract that is real money. Only the poster (~180 KB) loads with
 *     the page.
 *   - Native `controls`, so the audio can be muted, paused and seeked. Content
 *     that moves for more than five seconds has to be pausable (WCAG 2.2.2), and
 *     an autoplaying narrated video fails that by construction.
 *   - The poster carries a play affordance of our own, because a bare poster
 *     frame does not read as a video on every browser.
 */
export default function FilmSection() {
  const videoRef = useRef(null)
  const [started, setStarted] = useState(false)

  /*
   * Start playing once the <video> is actually in the DOM.
   *
   * This has to be an effect rather than something the click handler does: the
   * element does not exist until React commits the state change, so a `play()`
   * call inside the handler - or in a `requestAnimationFrame` after it - finds an
   * empty ref and silently does nothing, leaving the visitor with a video that
   * needs a second click. Autoplay policies are satisfied either way, because
   * the state change came from a real click.
   */
  useEffect(() => {
    if (!started) return
    videoRef.current?.play?.().catch(() => {
      /* Blocked for some other reason - the native controls are right there. */
    })
  }, [started])

  return (
    <Section id="film">
      <SectionHead
        eyebrow="Im Bild"
        title="Warum eine professionelle Verwaltung den Unterschied macht"
        lead="In einer Minute erklärt: was Eigentümer im Alltag beschäftigt - und wo wir die Arbeit übernehmen."
      />

      <Reveal>
        <figure className="film">
          <div className="film__frame">
            {started ? (
              <video
                ref={videoRef}
                className="film__video"
                controls
                playsInline
                preload="metadata"
                poster={POSTER}
              >
                <source src={VIDEO} type="video/mp4" />
                Ihr Browser kann dieses Video nicht abspielen.{' '}
                <a href={VIDEO}>Video herunterladen</a>.
              </video>
            ) : (
              <button
                type="button"
                className="film__poster"
                onClick={() => setStarted(true)}
                aria-label="Film abspielen: Warum eine professionelle Verwaltung den Unterschied macht (58 Sekunden, mit Ton)"
              >
                <img
                  src={POSTER}
                  alt=""
                  width="1600"
                  height="900"
                  loading="lazy"
                  decoding="async"
                />
                <span className="film__play" aria-hidden="true">
                  <Icon name="play" />
                </span>
                <span className="film__hint" aria-hidden="true">
                  Film ansehen · 58 Sek. · mit Ton
                </span>
              </button>
            )}
          </div>

          <figcaption className="film__caption">
            Der Film läuft mit Ton. Untertitel sind noch nicht hinterlegt - bei
            Fragen zum Inhalt rufen Sie uns gern an.
          </figcaption>
        </figure>
      </Reveal>
    </Section>
  )
}
